import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, CreditCard, CheckCircle, Shield, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentPage = () => {
  const { t } = useLanguage();
  const { user, hasPaid } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(false);
  
  // Get registration type from URL params
  const searchParams = new URLSearchParams(location.search);
  const registrationType = searchParams.get('type') || 'user'; // 'user' or 'professional'

  // If user is logged in and has paid, redirect
  useEffect(() => {
    if (user && hasPaid) {
      navigate('/directory');
    }
  }, [user, hasPaid, navigate]);

  const handlePayment = async () => {
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/payments/initiate-v2`, {
        origin_url: window.location.origin,
        payment_method: 'card'
      });
      
      if (response.data.url) {
        localStorage.setItem('registration_type', registrationType);
        window.location.href = response.data.url;
      } else {
        toast.error(t('common_error'));
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || t('common_error');
      toast.error(errorDetail);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { key: 'pay_features_1', icon: CheckCircle },
    { key: 'pay_features_2', icon: CheckCircle },
    { key: 'pay_features_3', icon: CheckCircle },
    { key: 'pay_features_4', icon: CheckCircle },
  ];

  // Get display amount
  const getDisplayAmount = () => {
    return '€2,50';
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="payment-page">
      <div className="max-w-lg mx-auto">
        <Card className="border border-slate-200 rounded-md shadow-sm">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-slate-900 rounded-sm flex items-center justify-center">
                <CreditCard className="h-8 w-8 text-orange-500" />
              </div>
            </div>
            <CardTitle 
              className="text-2xl font-semibold text-slate-900"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {t('pay_title')}
            </CardTitle>
            <p className="text-slate-600 mt-2">{t('pay_before_register')}</p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Registration Type Badge */}
            <div className="text-center">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                registrationType === 'professional' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-green-100 text-green-700'
              }`}>
                {registrationType === 'professional' ? t('auth_as_pro') : t('auth_as_user')}
              </span>
            </div>

            {/* Price - EUR */}
            <div className="text-center py-6 bg-slate-50 rounded-md border border-slate-200">
              <div>
                <span className="text-4xl font-bold text-orange-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {t('pay_amount')}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Quota di iscrizione iniziale / Initial registration fee</p>
            </div>
            
            {/* Features */}
            <ul className="space-y-3">
              {features.map((feature) => (
                <li key={feature.key} className="flex items-center gap-3">
                  <feature.icon className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">{t(feature.key)}</span>
                </li>
              ))}
            </ul>
            
            {/* Payment Method */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">
                Metodo di pagamento / Payment method:
              </p>
              <div className="grid grid-cols-1 gap-3">
                <button
                  type="button"
                  className="p-4 border border-orange-500 bg-orange-50 rounded-md"
                  data-testid="payment-card"
                >
                  <div className="flex flex-col items-center gap-2">
                    <CreditCard className="h-8 w-8 text-slate-700" />
                    <span className="font-medium text-slate-900">{t('pay_card')}</span>
                    <span className="text-xs text-slate-500">€2,50</span>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Pay Button */}
            <Button 
              className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm h-12 text-lg font-medium"
              onClick={handlePayment}
              disabled={loading}
              data-testid="payment-submit"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : null}
              {t('pay_card')} - {getDisplayAmount()}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            {/* Security Badge */}
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <Shield className="h-4 w-4" />
              <span>Pagamento sicuro / Secure payment</span>
            </div>

            {/* Stripe Logo */}
            <div className="flex justify-center">
              <svg className="h-8" viewBox="0 0 60 25" fill="none">
                <path d="M5.714 4.143h2.857v16.714H5.714V4.143zm-5.714 7.714c0-2.357 1.143-3.357 3.143-3.357.714 0 1.357.143 1.714.286v2.5c-.357-.143-.786-.215-1.143-.215-.857 0-1.143.357-1.143 1.286v.286h2.286v2.286H2.571v6.929H0V11.857z" fill="#635BFF"/>
                <path d="M12.857 8.857c1.714 0 2.571.786 2.857 2v-2.286h2.572v12.143h-2.572v-2.143c-.357 1.143-1.214 2-2.857 2-2.214 0-3.714-1.857-3.714-5.857s1.5-5.857 3.714-5.857zm.714 9.429c1.143 0 2-.714 2.143-2v-3.572c-.143-1.286-1-2-2.143-2-1.286 0-2 1-2 3.786s.714 3.786 2 3.786z" fill="#635BFF"/>
                <path d="M21.429 21.714c-2.286 0-3.572-1.286-3.572-3.286s1.214-3 3.357-3.143l2.5-.143v-.5c0-1.071-.5-1.5-1.571-1.5-.929 0-1.5.357-1.5 1.214h-2.572c.072-2 1.643-3.5 4.143-3.5 2.643 0 4.072 1.357 4.072 4v5.857h-2.572v-1.357c-.357.929-1.286 1.357-2.286 1.357zm.571-2.143c1.143 0 1.857-.714 1.857-1.857v-.857l-1.857.143c-1 .071-1.5.5-1.5 1.214 0 .857.572 1.357 1.5 1.357z" fill="#635BFF"/>
                <path d="M27.857 8.857h2.572v1.929c.5-1.357 1.5-2.143 2.857-2.143.357 0 .643.071.857.143v2.571c-.286-.071-.571-.143-1-.143-1.429 0-2.357.857-2.714 2.143v7.357h-2.572V8.857z" fill="#635BFF"/>
                <path d="M34.286 4.143h2.571v16.571h-2.571V4.143z" fill="#635BFF"/>
                <path d="M38.571 8.857H41.143v1.857c.429-1.286 1.357-2.143 2.857-2.143 1.857 0 3 1.143 3 3.571v8.572h-2.571v-7.857c0-1.286-.5-1.929-1.5-1.929-1.143 0-1.857.714-1.857 2.143v7.643h-2.572V8.857h.071z" fill="#635BFF"/>
                <path d="M55.714 18.143c-.5 2.357-2.286 3.571-4.857 3.571-3.143 0-5-2.143-5-5.857 0-3.786 2-5.857 4.929-5.857 2.857 0 4.714 1.786 4.714 5.214v.929h-7c.071 1.857.857 2.929 2.286 2.929 1.143 0 1.857-.5 2.143-1.5l2.786.571zm-7.143-4h4.5c-.071-1.643-.857-2.429-2.143-2.429-1.286 0-2.143.786-2.357 2.429z" fill="#635BFF"/>
              </svg>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentPage;
